import apiClient from '../lib/api/client';
import { getApiUrl, buildApiUrl } from '../config/config';
import { extractErrorMessage } from '../utils/errorHandler';

const createErrorResponse = (error, defaultMessage = 'An unexpected error occurred') => {
  const errorMessage = extractErrorMessage(
    error.response?.data || error,
    defaultMessage
  );

  return {
    isSuccess: false,
    data: null,
    error: {
      message: errorMessage,
      code: error.response?.status || 500,
      originalError: error,
    },
  };
};

const noReplyResolveService = {
  async getNoReplyResolveConfig() {
    try {
      const response = await apiClient.get(getApiUrl('AUTH', 'NO_REPLY_RESOLVE'));

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to fetch auto-resolve configuration' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to fetch auto-resolve configuration');
    }
  },

  async updateNoReplyResolveConfig(configData) {
    try {
      const response = await apiClient.patch(getApiUrl('AUTH', 'NO_REPLY_RESOLVE'), configData);

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to update auto-resolve configuration' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to update auto-resolve configuration');
    }
  },

  async activateNoReplyResolveConfig() {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'NO_REPLY_RESOLVE_ACTIVATE'));

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to activate auto-resolve inactive chats' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to activate auto-resolve inactive chats');
    }
  },

  async deactivateNoReplyResolveConfig() {
    try {
      const response = await apiClient.post(getApiUrl('AUTH', 'NO_REPLY_RESOLVE_DEACTIVATE'));

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to disable auto-resolve inactive chats' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to disable auto-resolve inactive chats');
    }
  },

  /**
   * Respond to an inactive-chat (no-reply) resolve prompt.
   * POST /api/chats/conversations/{conversationId}/no-reply-prompt/respond/
   *
   * @param {number|string} conversationId - Conversation the prompt belongs to (from socket / localStorage).
   * @param {{ promptId: number|string, resolve: boolean }} params
   */
  async respondToNoReplyPrompt(conversationId, { promptId, resolve }) {
    try {
      const url = buildApiUrl(`/api/chats/conversations/${conversationId}/no-reply-prompt/respond/`);
      const response = await apiClient.post(url, {
        prompt_id: promptId,
        resolve,
      });

      if (response.data?.isSuccess) {
        return {
          isSuccess: true,
          data: response.data.data,
          error: null,
        };
      }

      return {
        isSuccess: false,
        data: null,
        error: response.data?.error || { message: 'Failed to respond to inactive chat prompt' },
      };
    } catch (error) {
      return createErrorResponse(error, 'Failed to respond to inactive chat prompt');
    }
  },
};

export default noReplyResolveService;
